import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Divider,
    Card,
    CardContent,
    Grid,
    Stepper,
    Step,
    StepLabel,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    Slider,
    Stack,
    Chip,
    LinearProgress
} from '@mui/material';
import {
    getEnergyCostData,
    calculateEnergySetupCost,
    getNearestEnergyPotential,
    getLocationFromCoordinates,
    EnergyCostData,
    EnergySetupCostResponse,
    EnergyPotentialPoint
} from '../../services/api';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SolarPowerIcon from '@mui/icons-material/WbSunny';
import WindPowerIcon from '@mui/icons-material/Air';
import WaterIcon from '@mui/icons-material/Water';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import GrassIcon from '@mui/icons-material/Grass';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import CalculateIcon from '@mui/icons-material/Calculate';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SavingsIcon from '@mui/icons-material/Savings';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';

// List of countries and states for the dropdowns
const countries = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany',
    'France', 'Spain', 'Italy', 'Japan', 'China', 'India', 'Brazil'
];

// States by country (simplified)
const statesByCountry: Record<string, string[]> = {
    'United States': [
        'California', 'Texas', 'New York', 'Florida', 'Illinois',
        'Pennsylvania', 'Ohio', 'Michigan', 'Georgia', 'North Carolina'
    ],
    'Canada': [
        'Ontario', 'Quebec', 'British Columbia', 'Alberta', 'Manitoba',
        'Saskatchewan', 'Nova Scotia', 'New Brunswick'
    ],
    'India': [
        'Maharashtra', 'Tamil Nadu', 'Karnataka', 'Gujarat', 'Delhi',
        'Uttar Pradesh', 'West Bengal', 'Telangana', 'Rajasthan'
    ],
    // Default empty arrays for other countries
};

// Main component
const EnergyCalculator: React.FC = () => {
    // State for wizard steps
    const [activeStep, setActiveStep] = useState(0);

    // State for location
    const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
    const [locationLoading, setLocationLoading] = useState(false);
    const [country, setCountry] = useState<string>('');
    const [state, setState] = useState<string>('');
    const [locationMethod, setLocationMethod] = useState<'manual' | 'auto'>('auto');

    // State for energy data
    const [energyType, setEnergyType] = useState<'solar' | 'wind' | 'hydro' | 'geothermal' | 'biomass'>('solar');
    const [capacity, setCapacity] = useState<number>(10); // 10kW default
    const [investmentBudget, setInvestmentBudget] = useState<number | ''>('');
    const [landArea, setLandArea] = useState<number | ''>('');

    // State for API data
    const [costData, setCostData] = useState<EnergyCostData | null>(null);
    const [potentialData, setPotentialData] = useState<EnergyPotentialPoint | null>(null);
    const [calculationResult, setCalculationResult] = useState<EnergySetupCostResponse | null>(null);

    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Get user location
    const getUserLocation = useCallback(() => {
        setLocationLoading(true);
        setError(null);

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    setUserLocation([latitude, longitude]);
                    setLocationMethod('auto');

                    try {
                        // Get location information using multiple geocoding services with fallbacks
                        const locationInfo = await getLocationFromCoordinates(latitude, longitude);
                        console.log('Location data retrieved:', locationInfo);

                        // Set country and state from geocoding result if available
                        if (locationInfo.country) {
                            setCountry(locationInfo.country);
                        }
                        if (locationInfo.state) {
                            setState(locationInfo.state);
                        }

                        // Get energy potential for the location with real location data
                        try {
                            const potentialData = await getEnergyPotentialWithRetry(latitude, longitude);

                            // Update the location information with geocoding data
                            if (potentialData && locationInfo) {
                                if (locationInfo.country) {
                                    potentialData.country = locationInfo.country;
                                }
                                if (locationInfo.state) {
                                    potentialData.state = locationInfo.state;
                                }
                                if (locationInfo.city) {
                                    potentialData.region = locationInfo.city;
                                }
                            }

                            setPotentialData(potentialData);

                            // Get cost data for this country/state with retry
                            try {
                                const costData = await getEnergyCostDataWithRetry(potentialData.country, potentialData.state);
                                setCostData(costData);
                            } catch (error) {
                                console.error('Error fetching cost data:', error);
                                setError('Could not retrieve energy cost data for your location. Using default values.');

                                // Set default cost data so user can still proceed
                                const defaultCostData: EnergyCostData = {
                                    country: potentialData.country || locationInfo.country || 'Unknown',
                                    state: potentialData.state || locationInfo.state || 'Unknown',
                                    electricityPrice: 0.15, // Default electricity price
                                    averageConsumption: 10000, // Default consumption
                                    solarInstallationCost: 1500,
                                    windInstallationCost: 200000,
                                    hydroInstallationCost: 100000,
                                    geothermalInstallationCost: 250000,
                                    biomassInstallationCost: 180000,
                                    governmentIncentives: [
                                        {
                                            type: 'Default Incentive',
                                            description: 'Estimated incentive for renewable energy',
                                            value: 30
                                        }
                                    ],
                                    carbonCredit: 25
                                };
                                setCostData(defaultCostData);
                            }
                        } catch (error) {
                            console.error('Error fetching energy potential data:', error);
                            setError('Could not retrieve energy potential data for your location. Please try again or enter your location manually.');
                        }
                    } catch (error) {
                        console.error('Error getting location information:', error);
                        setError('Could not determine your location details. Using approximate location based on coordinates.');
                    }

                    setLocationLoading(false);
                },
                (error) => {
                    console.error('Error getting user location:', error);
                    setError('Could not access your location. Please check your browser permissions and try again or enter your location manually.');
                    setLocationMethod('manual');
                    setLocationLoading(false);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        } else {
            setError('Geolocation is not supported by your browser. Please enter your location manually.');
            setLocationMethod('manual');
            setLocationLoading(false);
        }
    }, []);

    // Initialize by getting user location
    useEffect(() => {
        getUserLocation();
    }, [getUserLocation]);

    // Handle country change
    const handleCountryChange = async (event: SelectChangeEvent) => {
        const selectedCountry = event.target.value;
        setCountry(selectedCountry);
        setState(''); // Reset state when country changes
        setLocationMethod('manual');

        try {
            const costData = await getEnergyCostData(selectedCountry);
            setCostData(costData);
        } catch (error) {
            console.error('Error fetching cost data:', error);
        }
    };

    // Handle state change
    const handleStateChange = async (event: SelectChangeEvent) => {
        const selectedState = event.target.value;
        setState(selectedState);

        if (country) {
            try {
                const costData = await getEnergyCostData(country, selectedState);
                setCostData(costData);
            } catch (error) {
                console.error('Error fetching cost data:', error);
            }
        }
    };

    // Handle energy type change
    const handleEnergyTypeChange = (event: SelectChangeEvent) => {
        setEnergyType(event.target.value as 'solar' | 'wind' | 'hydro' | 'geothermal' | 'biomass');
    };

    // Handle capacity change
    const handleCapacityChange = (event: Event, newValue: number | number[]) => {
        setCapacity(newValue as number);
    };

    // Handle budget change
    const handleBudgetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === '' ? '' : Number(event.target.value);
        setInvestmentBudget(value);
    };

    // Handle land area change
    const handleLandAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === '' ? '' : Number(event.target.value);
        setLandArea(value);
    };

    // Handle calculation submission
    const handleCalculate = async () => {
        if (!userLocation) {
            setError('Location data is required. Please use "Locate Me" or enter your location manually.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const [latitude, longitude] = userLocation;

            const request = {
                latitude,
                longitude,
                energyType,
                capacity,
                // Only include optional parameters if they have values
                ...(investmentBudget !== '' && { investmentBudget: Number(investmentBudget) }),
                ...(landArea !== '' && { landArea: Number(landArea) })
            };

            // Try calculation with retry logic
            let result: EnergySetupCostResponse | null = null;
            let calculationError: Error | null = null;

            for (let attempt = 0; attempt < 3; attempt++) {
                try {
                    console.log(`Attempting to calculate setup costs (attempt ${attempt + 1}/3)`);
                    result = await calculateEnergySetupCost(request);
                    break; // Success, exit loop
                } catch (error) {
                    console.error(`Calculation attempt ${attempt + 1} failed:`, error);
                    calculationError = error as Error;
                    // Wait between retries
                    if (attempt < 2) {
                        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
                    }
                }
            }

            if (result) {
                setCalculationResult(result);
                setActiveStep(3); // Move to results step
            } else {
                throw calculationError || new Error('Failed to calculate energy setup costs after multiple attempts');
            }
        } catch (error) {
            console.error('Error calculating energy setup costs:', error);
            setError('Failed to calculate energy setup costs. Please try again with different parameters.');
        } finally {
            setLoading(false);
        }
    };

    // Handle step navigation
    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    // Get the appropriate icon for each energy type
    const getEnergyIcon = (type: string) => {
        switch (type) {
            case 'solar': return <SolarPowerIcon />;
            case 'wind': return <WindPowerIcon />;
            case 'hydro': return <WaterIcon />;
            case 'geothermal': return <LocalFireDepartmentIcon />;
            case 'biomass': return <GrassIcon />;
            default: return null;
        }
    };

    // Step content components
    const LocationStep = () => (
        <Box>
            <Typography variant="h6" gutterBottom>
                Where would you like to set up your renewable energy system?
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid>
                    <RadioGroup
                        row
                        value={locationMethod}
                        onChange={(e) => setLocationMethod(e.target.value as 'manual' | 'auto')}
                    >
                        <FormControlLabel
                            value="auto"
                            control={<Radio />}
                            label="Use my current location"
                        />
                        <FormControlLabel
                            value="manual"
                            control={<Radio />}
                            label="Enter location manually"
                        />
                    </RadioGroup>
                </Grid>

                {locationMethod === 'auto' ? (
                    <Grid>
                        <Button
                        variant="contained"
                        startIcon={<MyLocationIcon />}
                        onClick={getUserLocation}
                        disabled={locationLoading}
                        sx={{
                            minHeight: 48,
                            borderRadius: 8,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 2,
                        }}
                        >
                        {locationLoading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
                        {locationLoading ? 'Getting Location...' : 'Locate Me'}
                        </Button>


                        {userLocation && (
                            <Box sx={{ mt: 2 }}>
                                <Alert
                                    severity="success"
                                    icon={<LocationOnIcon />}
                                    action={
                                        <Button
                                        size="small"
                                        color="inherit"
                                        onClick={getUserLocation}
                                        sx={{
                                            minHeight: 48,
                                            borderRadius: 8,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 2,
                                        }}
                                        >
                                        Refresh
                                        </Button>
                                    }
                                >
                                    <Typography variant="body2">
                                        <strong>Location detected:</strong> {potentialData?.region}, {potentialData?.state}, {potentialData?.country}
                                    </Typography>
                                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                                        Coordinates: {userLocation[0].toFixed(5)}, {userLocation[1].toFixed(5)}
                                    </Typography>
                                </Alert>
                            </Box>
                        )}
                    </Grid>
                ) : (
                    <>
                        <Grid>
                            <FormControl fullWidth>
                                <InputLabel id="country-label">Country</InputLabel>
                                <Select
                                    labelId="country-label"
                                    value={country}
                                    label="Country"
                                    onChange={handleCountryChange}
                                >
                                    {countries.map((c) => (
                                        <MenuItem key={c} value={c}>{c}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid>
                            <FormControl fullWidth disabled={!country || !statesByCountry[country]}>
                                <InputLabel id="state-label">State/Province</InputLabel>
                                <Select
                                    labelId="state-label"
                                    value={state}
                                    label="State/Province"
                                    onChange={handleStateChange}
                                >
                                    {(statesByCountry[country] || []).map((s) => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </>
                )}
            </Grid>

            {error && (
                <Box sx={{ mt: 2 }}>
                    <Alert
                        severity="warning"
                        action={
                            locationMethod === 'auto' ? (
                                <Button color="inherit" size="small" onClick={getUserLocation}>
                                    Retry
                                </Button>
                            ) : null
                        }
                    >
                        {error}
                    </Alert>
                    {locationMethod === 'auto' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pl: 2 }}>
                            If location detection continues to fail, please try the "Enter location manually" option.
                        </Typography>
                    )}
                </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
            variant="contained"
            onClick={handleNext}
            disabled={
                (locationMethod === 'auto' && !userLocation) ||
                (locationMethod === 'manual' && (!country || !state))
            }
            sx={{
                minHeight: 48,
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
            }}
            >
            Next
            </Button>
            </Box>
        </Box>
    );

    const EnergyTypeStep = () => (
        <Box>
            <Typography variant="h6" gutterBottom>
                Select your preferred renewable energy type
            </Typography>

            {potentialData && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Based on your location, we recommend:
                    <strong> {potentialData.recommendations.primarySource}</strong> (first choice) or
                    <strong> {potentialData.recommendations.secondarySource}</strong> (second choice)
                </Alert>
            )}

            <Grid container spacing={2}>
                {['solar', 'wind', 'hydro', 'geothermal', 'biomass'].map((type) => {
                    const selected = energyType === type;
                    const potential = typeof potentialData?.[type as keyof typeof potentialData] === 'object'
                        ? (potentialData?.[type as keyof typeof potentialData] as any)?.potential || 0
                        : 0;
                    const isPrimaryRecommended = potentialData?.recommendations.primarySource === type;
                    const isSecondaryRecommended = potentialData?.recommendations.secondarySource === type;

                    return (
                        <Grid>
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    borderWidth: selected ? 2 : 1,
                                    borderStyle: 'solid',
                                    borderColor: selected ? 'primary.main' : 'divider',
                                    bgcolor: selected ? 'action.selected' : 'background.paper',
                                    height: '100%'
                                }}
                                onClick={() => setEnergyType(type as any)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        {getEnergyIcon(type)}
                                        <Typography variant="h6" sx={{ ml: 1 }}>
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </Typography>
                                    </Box>

                                    {potentialData && (
                                        <Typography variant="body2" color="text.secondary">
                                            Potential in your area: {potential}%
                                        </Typography>
                                    )}

                                    <Box sx={{ mt: 1 }}>
                                        {isPrimaryRecommended && (
                                            <Chip
                                                label="Recommended"
                                                color="success"
                                                size="small"
                                                sx={{ mr: 1 }}
                                            />
                                        )}
                                        {isSecondaryRecommended && (
                                            <Chip
                                                label="Alternative"
                                                color="info"
                                                size="small"
                                            />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={handleBack}>
                    Back
                </Button>
                <Button variant="contained" onClick={handleNext}>
                    Next
                </Button>
            </Box>
        </Box>
    );

    const ParametersStep = () => (
        <Box>
            <Typography variant="h6" gutterBottom>
                System Setup Parameters
            </Typography>

            <Grid container spacing={3}>
                <Grid>
                    <Typography id="capacity-slider" gutterBottom>
                        System Capacity (kW)
                    </Typography>
                    <Slider
                        value={capacity}
                        onChange={handleCapacityChange}
                        aria-labelledby="capacity-slider"
                        valueLabelDisplay="auto"
                        step={5}
                        marks
                        min={5}
                        max={energyType === 'solar' ? 100 : 500}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {energyType === 'solar' && 'A typical home solar installation is around 5-10kW'}
                        {energyType === 'wind' && 'Small wind turbines range from 10kW to 100kW'}
                        {energyType === 'hydro' && 'Micro hydro systems range from 5kW to 100kW'}
                        {energyType === 'geothermal' && 'Residential systems typically range from 5kW to 50kW'}
                        {energyType === 'biomass' && 'Small biomass systems start at around 10kW'}
                    </Typography>
                </Grid>

                <Grid>
                    <TextField
                        fullWidth
                        label="Investment Budget (optional)"
                        type="number"
                        value={investmentBudget}
                        onChange={handleBudgetChange}
                        InputProps={{
                            startAdornment: <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                        helperText="Your maximum budget for this project"
                    />
                </Grid>

                <Grid>
                    <TextField
                        fullWidth
                        label="Available Land Area (sq meters, optional)"
                        type="number"
                        value={landArea}
                        onChange={handleLandAreaChange}
                        helperText="Land area available for installation"
                    />
                </Grid>

                {costData && (
                    <Grid>
                        <Card sx={{ mt: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle1">
                                    Energy Market Data for {costData.state}, {costData.country}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2">
                                        • Average electricity price: ${costData.electricityPrice.toFixed(2)} per kWh
                                    </Typography>
                                    <Typography variant="body2">
                                        • Average annual consumption: {costData.averageConsumption.toLocaleString()} kWh
                                    </Typography>
                                    <Typography variant="body2">
                                        • {energyType.charAt(0).toUpperCase() + energyType.slice(1)} installation cost:
                                        ${getInstallationCost().toLocaleString()} per {energyType === 'solar' ? 'kW' : 'system'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={handleBack}>
                    Back
                </Button>
                <Button
                    variant="contained"
                    onClick={handleCalculate}
                    startIcon={<CalculateIcon />}
                    disabled={loading}
                >
                    Calculate Profitability
                </Button>
            </Box>

            {loading && <LinearProgress sx={{ mt: 2 }} />}

            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}
        </Box>
    );

    const ResultsStep = () => {
        if (!calculationResult) {
            return (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>
                        Loading calculation results...
                    </Typography>
                </Box>
            );
        }

        const {
            initialCost, annualProduction, annualSavings, paybackPeriod,
            roi, co2Reduction, netCost, maintenanceCost, lifespanYears,
            totalIncentives, breakdown, longTermProfit, recommendations
        } = calculationResult;

        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    Energy System Profitability Analysis
                </Typography>

                <Grid container spacing={3}>
                    <Grid>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle1" gutterBottom>
                                    <AccountBalanceIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                    Investment Summary
                                </Typography>

                                <Grid container spacing={1}>
                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Initial Cost:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2" fontWeight="bold">
                                            ${initialCost.toLocaleString()}
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Government Incentives:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2" fontWeight="bold" color="success.main">
                                            ${totalIncentives.toLocaleString()}
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Net Cost After Incentives:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2" fontWeight="bold">
                                            ${netCost.toLocaleString()}
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Annual Maintenance:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2">
                                            ${maintenanceCost.toLocaleString()}/year
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            System Lifespan:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2">
                                            {lifespanYears} years
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2" gutterBottom>
                                    Cost Breakdown
                                </Typography>

                                <Grid container spacing={1}>
                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Equipment:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2">
                                            ${breakdown.equipmentCost.toLocaleString()}
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Installation:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2">
                                            ${breakdown.installationCost.toLocaleString()}
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Permits & Fees:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2">
                                            ${breakdown.permitsCost.toLocaleString()}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle1" gutterBottom>
                                    <SavingsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                    Return on Investment
                                </Typography>

                                <Grid container spacing={1}>
                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Annual Energy Production:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2" fontWeight="bold">
                                            {annualProduction.toLocaleString()} kWh
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Annual Savings:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2" fontWeight="bold" color="success.main">
                                            ${annualSavings.toLocaleString()}/year
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Return on Investment:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2" fontWeight="bold" color="success.main">
                                            {roi.toFixed(1)}%
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            Payback Period:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2" fontWeight="bold">
                                            {paybackPeriod.toFixed(1)} years
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            CO₂ Reduction:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2">
                                            {co2Reduction.toFixed(1)} tons/year
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2" gutterBottom>
                                    Long-term Profit
                                </Typography>

                                <Grid container spacing={1}>
                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            After 5 years:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2" color={longTermProfit.year5 > 0 ? 'success.main' : 'error.main'}>
                                            {longTermProfit.year5 > 0 ? '+' : ''}{longTermProfit.year5.toLocaleString()} USD
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            After 10 years:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2" color={longTermProfit.year10 > 0 ? 'success.main' : 'error.main'}>
                                            {longTermProfit.year10 > 0 ? '+' : ''}{longTermProfit.year10.toLocaleString()} USD
                                        </Typography>
                                    </Grid>

                                    <Grid>
                                        <Typography variant="body2" color="text.secondary">
                                            After 25 years:
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="body2" color={longTermProfit.year25 > 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                                            {longTermProfit.year25 > 0 ? '+' : ''}{longTermProfit.year25.toLocaleString()} USD
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" gutterBottom>
                                    <EnergySavingsLeafIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                    AI Recommendations
                                </Typography>

                                <Stack spacing={1}>
                                    {recommendations.map((recommendation, index) => (
                                        <Alert key={index} severity="info" sx={{ py: 0.5 }}>
                                            {recommendation}
                                        </Alert>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={() => setActiveStep(0)}>
                        Start Over
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            // Reset some parameters but keep location data
                            setEnergyType('solar');
                            setCapacity(10);
                            setInvestmentBudget('');
                            setLandArea('');
                            setCalculationResult(null);
                            setActiveStep(1); // Go back to energy type selection
                        }}
                    >
                        Try Different Energy Type
                    </Button>
                </Box>
            </Box>
        );
    };

    // Helper function to get installation cost based on energy type
    const getInstallationCost = () => {
        if (!costData) return 0;

        switch (energyType) {
            case 'solar': return costData.solarInstallationCost;
            case 'wind': return costData.windInstallationCost;
            case 'hydro': return costData.hydroInstallationCost;
            case 'geothermal': return costData.geothermalInstallationCost;
            case 'biomass': return costData.biomassInstallationCost;
            default: return 0;
        }
    };

    // Helper function to retry getting energy potential data
    const getEnergyPotentialWithRetry = async (latitude: number, longitude: number, maxRetries = 3): Promise<EnergyPotentialPoint> => {
        let lastError;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                console.log(`Attempting to get energy potential data (attempt ${attempt + 1}/${maxRetries})`);
                const data = await getNearestEnergyPotential(latitude, longitude);
                return data;
            } catch (error) {
                console.error(`Attempt ${attempt + 1} failed:`, error);
                lastError = error;
                // Wait between retries (increasing delay with each attempt)
                if (attempt < maxRetries - 1) {
                    await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
                }
            }
        }

        // If all retries fail, throw the last error
        throw lastError || new Error('Failed to get energy potential data after multiple attempts');
    };

    // Helper function to retry getting energy cost data
    const getEnergyCostDataWithRetry = async (country: string, state?: string, maxRetries = 3): Promise<EnergyCostData> => {
        let lastError;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                console.log(`Attempting to get energy cost data (attempt ${attempt + 1}/${maxRetries})`);
                const data = await getEnergyCostData(country, state);
                return data;
            } catch (error) {
                console.error(`Attempt ${attempt + 1} failed:`, error);
                lastError = error;
                // Wait between retries (increasing delay with each attempt)
                if (attempt < maxRetries - 1) {
                    await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
                }
            }
        }

        // If all retries fail, throw the last error
        throw lastError || new Error('Failed to get energy cost data after multiple attempts');
    };

    // Define steps
    const steps = [
        'Location',
        'Energy Type',
        'System Parameters',
        'Results'
    ];

    return (
        <Box>
            <Typography variant="h4" gutterBottom display="flex" alignItems="center">
                <CalculateIcon sx={{ mr: 1 }} />
                Renewable Energy Solutions
            </Typography>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Typography
                    variant="h6"
                    component="div"
                    sx={{
                        py: 1,
                        px: 2,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                        display: 'inline-block'
                    }}
                >
                    ENERGY ROI CALCULATOR
                </Typography>
            </Box>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Global Energy Calculator
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    Discover the most profitable renewable energy setup for your location with detailed ROI analysis
                </Typography>

                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box>
                    {activeStep === 0 && <LocationStep />}
                    {activeStep === 1 && <EnergyTypeStep />}
                    {activeStep === 2 && <ParametersStep />}
                    {activeStep === 3 && <ResultsStep />}
                </Box>
            </Paper>
        </Box>
    );
};

export default EnergyCalculator; 